// fin_payment — the manual PaymentIntent (the §6.3 machine's manual
// specialization: the VERIFIER'S APPROVAL plays the role the signature-verified
// webhook plays for gateways). v1 is provider='manual' only; provider /
// provider_ref / extra_json columns are locked now so gateway adapters slot in
// without a migration.
import { z } from "zod";
import { CursorQuery, EntityId, EpochMs } from "@erp/core-schemas";
import { PaymentStatus } from "./payment-status.js";
import { PositivePaise } from "./invoices.js";

export const PAYMENT_METHODS = ["upi", "cash", "dd", "bank"] as const;
export const PaymentMethod = z.enum(PAYMENT_METHODS);
export type PaymentMethod = z.infer<typeof PaymentMethod>;

const shared = {
  method: PaymentMethod,
  amountPaise: PositivePaise,
  /** UTR / DD no / bank ref — the instrument reference the verifier checks against the statement. */
  reference: z.string().trim().min(1).max(120).optional(),
  /** When the payer says the money moved (epoch ms). */
  paidOn: EpochMs,
  /** Which receiving instrument was paid into — optional for cash. */
  accountId: EntityId.optional(),
  /** The physical receipt-book number cash desks already use (source semantics preserved; free text). */
  manualReceiptRef: z.string().trim().min(1).max(60).optional(),
  note: z.string().trim().max(500).optional(),
  /** 0..N COMPLETED fin uploads of kind 'proof' (more attachable while pending_verification). */
  proofUploadIds: z.array(EntityId).max(10).default([]),
};

/** POST /payments — the PAYER's claim (session.userId must equal invoice.payer_user_id). */
export const SubmitPaymentBody = z.object({
  invoiceId: EntityId,
  ...shared,
});
export type SubmitPaymentBody = z.infer<typeof SubmitPaymentBody>;

/** POST /payments/record — the STAFF desk path (fin:manage OR the invoice-module's :desk/:admin). */
export const RecordPaymentBody = z.object({
  invoiceId: EntityId,
  ...shared,
});
export type RecordPaymentBody = z.infer<typeof RecordPaymentBody>;

/** POST /payments/:id/proofs — attach one more proof to the caller's OWN pending payment. */
export const AttachProofBody = z.object({
  uploadId: EntityId,
});
export type AttachProofBody = z.infer<typeof AttachProofBody>;

/** POST /payments/:id/verify — the checker's approval. Note is optional context; the flip itself is the act. */
export const VerifyPaymentBody = z.object({
  note: z.string().trim().max(500).optional(),
});
export type VerifyPaymentBody = z.infer<typeof VerifyPaymentBody>;

/** POST /payments/:id/decline — the reason is REQUIRED (the source's checker-note law); terminal. */
export const DeclinePaymentBody = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type DeclinePaymentBody = z.infer<typeof DeclinePaymentBody>;

export const VERIFICATION_TABS = ["pending", "verified", "declined"] as const;
export const VerificationTab = z.enum(VERIFICATION_TABS);
export type VerificationTab = z.infer<typeof VerificationTab>;

/** GET /verification — the checker queue (tabbed keyset list; module auto-scoped for lib-rule verifiers). */
export const VerificationListQuery = CursorQuery.extend({
  tab: VerificationTab.default("pending"),
  module: z.string().trim().min(1).max(16).optional(),
  /** Matches invoice_no / payer_name / payment reference. */
  search: z.string().trim().min(1).max(120).optional(),
});
export type VerificationListQuery = z.infer<typeof VerificationListQuery>;

export const FinProofDto = z.object({
  id: EntityId,
  paymentId: EntityId,
  contentType: z.string(),
  sizeBytes: z.number().int(),
  uploadedAt: EpochMs,
});
export type FinProofDto = z.infer<typeof FinProofDto>;

export const FinPaymentDto = z.object({
  id: EntityId,
  invoiceId: EntityId,
  provider: z.string(),
  method: PaymentMethod,
  amountPaise: z.number().int(),
  reference: z.string().nullable(),
  paidOn: EpochMs.nullable(),
  accountId: z.string().nullable(),
  manualReceiptRef: z.string().nullable(),
  note: z.string().nullable(),
  status: PaymentStatus,
  submittedBy: z.string().nullable(),
  submittedAt: EpochMs,
  verifiedBy: z.string().nullable(),
  verifiedAt: EpochMs.nullable(),
  rejectedReason: z.string().nullable(),
  receiptNo: z.string().nullable(),
  settledAt: EpochMs.nullable(),
  settlementError: z.string().nullable(),
  proofs: z.array(FinProofDto),
});
export type FinPaymentDto = z.infer<typeof FinPaymentDto>;

/** GET /verification/stats — the console KPI row (FeeReconciliation port: net = max(0, gross − refunded)). */
export const VerificationStats = z.object({
  pendingCount: z.number().int(),
  verifiedCount: z.number().int(),
  declinedCount: z.number().int(),
  /** Σ amount over pending_verification claims — money waiting on a checker. */
  awaitingPaise: z.number().int(),
  /** Net collected all-time: max(0, Σ paid − Σ refunded). */
  netCollectedPaise: z.number().int(),
  /** Net collected with verified_at inside the current fiscal year. */
  netCollectedFyPaise: z.number().int(),
});
export type VerificationStats = z.infer<typeof VerificationStats>;
